/**
 * Tab-completion for the REPL line editor (readline `completer`). Two modes:
 *  - `/cmd`  — at line start → every slash name; mid-prompt → only `agentCommands` (see below).
 *  - `@path` — the token under the cursor → complete file/dir paths (dirs get a trailing `/`).
 * Returns readline's `[hits, token]`: `hits` replace the trailing `token`. No match → `[[], line]`.
 *
 * Synchronous by necessity: Bun/Node readline destructure the completer's return value inline,
 * so an async completer crashes. Directory access is injected as a sync `DirLister` — the CLI
 * backs it with `readdirSync`; tests pass an in-memory map.
 */

/** List an absolute dir's immediate entries for completion; return `null` if it isn't a directory. */
export type DirLister = (absDir: string) => Array<{ name: string; dir: boolean }> | null;

export interface CompleteContext {
  /** Slash-command names WITHOUT the leading `/` (builtins + user-defined) — offered at line start, where the REPL dispatches them. */
  commands: string[];
  /**
   * Subset offered MID-prompt: names the MODEL can act on via its `SlashCommand`/`Skill` tools
   * (skills + custom commands). Builtins are REPL-local — `/exit` inside a sentence is dead text
   * that only looks actionable. Defaults to `commands` when unset.
   */
  agentCommands?: string[];
  listDir: DirLister;
  /** Past `!`-prefixed shell lines (most-recent-first) — completes `!<partial>` from history (CC parity). */
  bangHistory?: string[];
}

/** The trailing whitespace-free token under the cursor — what we try to complete. */
export function lastToken(line: string): string {
  const m = line.match(/(\S*)$/);
  return m ? m[1] : '';
}

/**
 * Is `token` the line's first word? Only there does the REPL dispatch a `/cmd` (it matches the WHOLE
 * buffer against `/`), so only there may Enter accept-and-run. Shared with the line editor.
 */
export function atLineStart(line: string, token: string): boolean {
  return line.slice(0, line.length - token.length).trim() === '';
}

export function completeLine(line: string, ctx: CompleteContext): [string[], string] {
  const token = lastToken(line);

  // /slash-command. At line start the REPL dispatches it, so offer everything. Mid-prompt it's just
  // text the model reads, so offer only what the model can run (`agentCommands`). Non-command
  // slashes ('/usr/bin/x', '12/25') fuzzy-match nothing → no hits → the menu stays shut.
  if (token.startsWith('/')) {
    const pool = atLineStart(line, token) ? ctx.commands : (ctx.agentCommands ?? ctx.commands);
    const want = token.slice(1);
    const hits = rank(pool.filter((c) => fuzzy(want, c)), want).map((c) => '/' + c);
    return [hits, token];
  }

  // !shell — complete the WHOLE line from past `!` commands (needs ≥1 char so a bare `!` stays quiet).
  if (line.startsWith('!') && line.length > 1 && ctx.bangHistory?.length) {
    const want = line.slice(1);
    const seen = new Set<string>();
    const hits = ctx.bangHistory.filter((h) => h.startsWith('!') && h.length > 1 && fuzzy(want, h.slice(1)) && !seen.has(h) && !!seen.add(h));
    return [rank(hits, line), line]; // token = the whole line → accept replaces it entirely
  }

  // @file mention — complete a path.
  if (token.startsWith('@')) return [completePath(ctx.listDir, token.slice(1)), token];

  return [[], line];
}

/** Case-insensitive subsequence match (`cr` matches `code-review`) — empty needle matches all. */
export function fuzzy(needle: string, hay: string): boolean {
  if (!needle) return true;
  const n = needle.toLowerCase(), h = hay.toLowerCase();
  let i = 0;
  for (let j = 0; j < h.length && i < n.length; j++) if (h[j] === n[i]) i++;
  return i === n.length;
}

/** Sort fuzzy hits: exact-prefix matches first, then alphabetical — so the obvious match leads. */
function rank(hits: string[], needle: string): string[] {
  const n = needle.toLowerCase();
  return hits.slice().sort((a, b) => {
    const pa = a.toLowerCase().startsWith(n) ? 0 : 1, pb = b.toLowerCase().startsWith(n) ? 0 : 1;
    return pa - pb || a.localeCompare(b);
  });
}

/** Path completion: `@`-prefixed matches under `ref`'s dir (fuzzy basename), dirs suffixed with `/`. */
function completePath(listDir: DirLister, ref: string): string[] {
  const slash = ref.lastIndexOf('/');
  const dir = slash >= 0 ? ref.slice(0, slash) : '';
  const base = slash >= 0 ? ref.slice(slash + 1) : ref;
  const abs = '/' + dir.split('/').filter(Boolean).join('/'); // '/' when dir is empty
  const entries = listDir(abs);
  if (!entries) return [];
  const matched: string[] = [];
  for (const e of entries) {
    if (!fuzzy(base, e.name)) continue;
    if (e.name.startsWith('.') && !base.startsWith('.')) continue; // hide dotfiles unless explicitly typed
    const rel = (dir ? `${dir}/${e.name}` : e.name) + (e.dir ? '/' : '');
    // spaced names ride the quoted-mention form (@"a b.png") — an unquoted space breaks @\S+ parsing
    matched.push(/\s/.test(rel) ? `@"${rel}"` : '@' + rel);
  }
  return rank(matched, '@' + (dir ? dir + '/' : '') + base); // prefix-first within the dir
}
